import * as React from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { X as CloseIcon } from "@phosphor-icons/react/X";

import type { QueryField, QueryResults } from "../types";
import { CompactPagination } from "./compact-pagination";
import { ResultsTable } from "./results-table";

export interface FullscreenTableProps {
	/** Whether the fullscreen table is open */
	open: boolean;
	/** Callback when close button is clicked */
	onClose: () => void;
	/** Query results data */
	results: QueryResults;
	/** Query fields for column configuration */
	queryFields?: QueryField[];
	/** Callback when a row is clicked for feature highlighting */
	onRowClick?: (row: any) => void;
	/** Currently selected row for highlighting */
	selectedRowId?: number | string;
	/** Array of column names to exclude from results table */
	excludeColumns?: string[];
}

export function FullscreenTable({
	open,
	onClose,
	results,
	queryFields = [],
	onRowClick,
	selectedRowId,
	excludeColumns = [],
}: FullscreenTableProps): React.JSX.Element {
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(25);

	const handleChangePage = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(Number.parseInt(event.target.value, 10));
		setPage(0);
	};

	// Create paginated results for the fullscreen table
	const paginatedResults = React.useMemo(() => {
		const start = page * rowsPerPage;
		const end = start + rowsPerPage;
		return {
			...results,
			data: results.data.slice(start, end),
		};
	}, [results, page, rowsPerPage]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			fullScreen
			PaperProps={{
				sx: {
					bgcolor: "var(--mui-palette-background-level1)",
					display: "flex",
					flexDirection: "column",
				},
			}}>
			{/* Header with close button */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					p: 2,
					borderBottom: 1,
					borderColor: "divider",
					bgcolor: "var(--mui-palette-background-paper)",
				}}>
				<Typography variant="h6">
					{results.queryConfig.displayName}:{" "}
					{results.resultCount.toLocaleString()} results
				</Typography>
				<IconButton
					onClick={onClose}
					size="small">
					<CloseIcon size={20} />
				</IconButton>
			</Box>

			{/* Main content area */}
			<Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
				<Paper
					sx={{
						border: "1px solid var(--mui-palette-divider)",
						boxShadow: "var(--mui-shadows-16)",
						flex: 1,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}>
					{/* Table container */}
					<Box sx={{ flex: 1, overflow: "hidden" }}>
						<ResultsTable
							results={paginatedResults}
							queryFields={queryFields}
							pageSize={rowsPerPage}
							hideInternalPagination
							onRowClick={onRowClick}
							selectedRowId={selectedRowId}
							excludeColumns={excludeColumns}
						/>
					</Box>

					{/* Pagination at bottom */}
					<Box sx={{ borderTop: 1, borderColor: "divider" }}>
						<CompactPagination
							count={results.data.length}
							page={page}
							rowsPerPage={rowsPerPage}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[10, 25, 50, 100]}
						/>
					</Box>
				</Paper>
			</Box>
		</Dialog>
	);
}
